import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { CustomerOrderResponse } from '@/api/bol';
import {
  formatDateTime,
  getEntityDisplayName,
  formatExternalReferences,
  formatSpecialCharacteristics,
} from '@/utils/datagridFormatters';

interface CustomerOrderColumnsProps {
  onDelete: (customerOrder: CustomerOrderResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createCustomerOrderColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: CustomerOrderColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'creationDate',
    headerName: 'Creation Date',
    flex: 1.1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { creationDate?: string }) => formatDateTime(row.creationDate),
  },
  {
    field: 'buyer',
    headerName: 'Buyer',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { buyer?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.buyer),
  },
  {
    field: 'seller',
    headerName: 'Seller',
    flex: 1.2,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { seller?: { commercialName?: string | null; legalEntityName?: string | null; name?: string | null } }) =>
      getEntityDisplayName(row.seller),
  },
  {
    field: 'customerOrderLinesCount',
    headerName: 'Lines',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { customerOrderLines?: unknown[] }) => row.customerOrderLines?.length || 0,
  },
  {
    field: 'consignmentsCount',
    headerName: 'Consignments',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { consignments?: unknown[] }) => row.consignments?.length || 0,
  },
  {
    field: 'contactsCount',
    headerName: 'Contacts',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { contacts?: unknown[] }) => row.contacts?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.5,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'specialCharacteristics',
    headerName: 'Special Characteristics',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (
      _value: unknown,
      row: {
        specialCharacteristics?: Array<{
          specialCharacteristicType?: string | null;
          characteristicReference?: string | null;
          stringValue?: string | null;
          numericalValue?: { value?: number; unit?: string } | null;
        }>;
      }
    ) => formatSpecialCharacteristics(row.specialCharacteristics),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/customer-orders/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as CustomerOrderResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultCustomerOrderColumnVisibilityModel = {
  id: true,
  creationDate: true,
  buyer: true,
  seller: true,
  customerOrderLinesCount: true,
  consignmentsCount: true,
  contactsCount: false,
  externalReferences: false,
  specialCharacteristics: false,
};
